import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import { useCart } from "../lib/cart";
import { useWishlist } from "../lib/wishlist";
import { useSEO } from "../lib/seo";
import { toast } from "sonner";
import BlurHashImage from "../components/BlurHashImage";
import ShareButton from "../components/ShareButton";
import {
  ShoppingBag,
  ArrowLeft,
  Heart,
  Ruler,
} from "lucide-react";

/*
  Sizes are fixed by product type.
  Colours are NOT hard-coded here.
  Colours come directly from Admin Dashboard
  through the design.colors field.
*/



function imageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

const PRODUCT_SIZES = {
  "Round Neck T-Shirt": [
    "S",
    "M",
    "L",
    "XL",
    "XXL",
    "3XL",
  ],

  "Collar T-Shirt": [
    "S",
    "M",
    "L",
    "XL",
    "XXL",
    "3XL",
  ],

  "Oversized T-Shirt": [
    "S",
    "M",
    "L",
    "XL",
    "XXL",
    "3XL",
  ],

  Hoodies: [
    "S",
    "M",
    "L",
    "XL",
    "XXL",
  ],

  "Sports Jerseys": [
    "S",
    "M",
    "L",
    "XL",
    "XXL",
    "3XL",
  ],

  "Couple T-Shirt": [
    "S/S",
    "M/M",
    "L/L",
    "XL/XL",
    "XXL/XXL",
  ],
};

export default function Products() {
  const [params] = useSearchParams();

  const designId =
    params.get("design") || "";

  const { addItem } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();

  const [design, setDesign] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [size, setSize] =
    useState("");

  const [color, setColor] =
    useState("");

  const [quantity, setQuantity] =
    useState(1);

  const [quality, setQuality] =
    useState("");

  const [adding, setAdding] =
    useState(false);

  const [viewSide, setViewSide] = useState("front");
  const [selectedImage, setSelectedImage] = useState("");
  const [showSizeChart, setShowSizeChart] = useState(false);
  const sizeChartAreaRef = useRef(null);

  useEffect(() => {
    if (!showSizeChart) return undefined;

    const closeOnOutsidePointer = (event) => {
      if (sizeChartAreaRef.current && !sizeChartAreaRef.current.contains(event.target)) {
        setShowSizeChart(false);
      }
    };
    const closeOnViewportMove = () => setShowSizeChart(false);

    document.addEventListener("pointerdown", closeOnOutsidePointer, true);
    window.addEventListener("scroll", closeOnViewportMove, true);
    window.addEventListener("touchmove", closeOnViewportMove, { passive: true });
    window.addEventListener("resize", closeOnViewportMove);

    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
      window.removeEventListener("scroll", closeOnViewportMove, true);
      window.removeEventListener("touchmove", closeOnViewportMove);
      window.removeEventListener("resize", closeOnViewportMove);
    };
  }, [showSizeChart]);
  const [sharedSizeChart, setSharedSizeChart] = useState(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useSEO({
    title: "Buy Design — PrintMyTees",
    description:
      "Choose your size, colour and quantity and buy your PrintMyTees design online.",
  });

  const galleryImages = useMemo(
    () =>
      Array.from(
        new Set(
          [
            design?.image_url,
            design?.back_image_url,
            ...(Array.isArray(design?.images) ? design.images : []),
          ]
            .filter(Boolean)
            .map(imageUrl)
            .filter(Boolean)
        )
      ).slice(0, 5),
    [design]
  );

  const handleGalleryTouchStart = (event) => {
    if (galleryImages.length < 2) return;

    const touch = event.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  };

  const handleGalleryTouchEnd = (event) => {
    if (
      galleryImages.length < 2 ||
      touchStartX.current == null ||
      touchStartY.current == null
    ) {
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;

    touchStartX.current = null;
    touchStartY.current = null;

    if (
      Math.abs(deltaX) < 50 ||
      Math.abs(deltaX) <= Math.abs(deltaY)
    ) {
      return;
    }

    const currentIndex = Math.max(
      0,
      galleryImages.indexOf(selectedImage)
    );

    const nextIndex =
      deltaX < 0
        ? (currentIndex + 1) % galleryImages.length
        : (currentIndex - 1 + galleryImages.length) %
          galleryImages.length;

    setSelectedImage(galleryImages[nextIndex]);
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/size-chart").then(({ data }) => { if (!cancelled) setSharedSizeChart(data?.size_chart || null); })
      .catch(() => { if (!cancelled) setSharedSizeChart(null); });
    return () => { cancelled = true; };
  }, []);

  /*
    Load selected design
  */

  useEffect(() => {
    if (!designId) {
      setDesign(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    api
      .get(`/designs/${encodeURIComponent(designId)}`)
      .then((response) => {
        setDesign(response.data);
        setSelectedImage(imageUrl(response.data.image_url || response.data.back_image_url || response.data.images?.[0] || ""));
      })
      .catch(() => {
        setDesign(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [designId]);

  /*
    Product type
  */

  const group =
    design?.group ||
    "Round Neck T-Shirt";

  // Shared measurements are managed once in Admin > Size Charts.
  const sizeChart = sharedSizeChart && Array.isArray(sharedSizeChart.headers) && sharedSizeChart.headers.length > 0 && Array.isArray(sharedSizeChart.rows) && sharedSizeChart.rows.length > 0 ? sharedSizeChart : null;

  /*
    Sizes are determined by product type.
  */

  const sizes =
    PRODUCT_SIZES[group] ||
    PRODUCT_SIZES["Round Neck T-Shirt"];

  /*
    IMPORTANT:
    Colours come from Admin Dashboard.

    Example:
    Admin enters:
    Black, White, Cream, Navy

    Backend returns:
    ["Black", "White", "Cream", "Navy"]

    Customer sees exactly those colours.
  */

  const colors = useMemo(() => {
    if (
      !design ||
      !Array.isArray(design.colors)
    ) {
      return [];
    }

    return design.colors
      .map((colorItem) =>
        String(colorItem).trim()
      )
      .filter(Boolean);
  }, [design]);

  /*
    Price also comes from Admin Dashboard.
  */

  const qualities = useMemo(
    () => (Array.isArray(design?.qualities) ? design.qualities : []),
    [design]
  );

  const qualityName = (entry) =>
    typeof entry === "string"
      ? entry
      : String(entry?.name || "");

  const qualityPrice = (entry) => {
    if (!entry || typeof entry === "string") {
      return null;
    }
    const value = Number(entry.price);
    return Number.isFinite(value) && value > 0
      ? value
      : null;
  };

  const selectedQualityOption = qualities.find(
    (entry) => qualityName(entry) === quality
  );

  const selectedQualityPrice = qualityPrice(
    selectedQualityOption
  );

  const baseUnitPrice =
    selectedQualityPrice ?? Number(design?.price || 0);
  const discountPercent = Number(design?.discount_percent || 0);
  const unitPrice =
    baseUnitPrice > 0
      ? Math.round(baseUnitPrice * (1 - Math.min(100, Math.max(0, discountPercent)) / 100))
      : 0;

  const safeQuantity =
    Number(quantity) > 0
      ? Number(quantity)
      : 1;

  const totalPrice =
    unitPrice * safeQuantity;

  /*
    Set first available size/colour
    when design loads.
  */

  useEffect(() => {
    if (!design) return;

    const availableSizes =
      PRODUCT_SIZES[
        design.group
      ] ||
      PRODUCT_SIZES[
        "Round Neck T-Shirt"
      ];

    setSize(
      availableSizes?.[0] || ""
    );

    setColor(
      Array.isArray(
        design.colors
      )
        ? design.colors[0] || ""
        : ""
    );

    setQuantity(1);

    const firstQuality = qualities[0];
    setQuality(qualityName(firstQuality));
  }, [design, qualities]);

  /*
    Add design to cart using the server-calculated price.
    The selected GSM is included when configured.
  */

  const handleAddToCart = async () => {
    if (!design) {
      return;
    }

    if (!unitPrice) {
      toast.error("This design does not have a price configured yet.");
      return;
    }

    if (!size) {
      toast.error("Please select a size.");
      return;
    }

    if (!color) {
      toast.error("Please select a colour.");
      return;
    }

    if (
      !Number(quantity) ||
      Number(quantity) < 1
    ) {
      toast.error("Please select a valid quantity.");
      return;
    }

    setAdding(true);

    try {
      const priced = await api.post("/product-price", {
        product_slug: `design-${design.design_id}`,
        design_id: design.design_id,
        product_name: design.name,
        product_type: group,
        size,
        color,
        quality: quality || null,
        quantity: Number(quantity),
      });

      addItem({
        product_slug:
          `design-${design.design_id}`,

        product_name:
          design.name,

        image:
          imageUrl(design.image_url || design.images?.[0] || null) || null,

        blurhash:
          design.blurhash || null,

        size: size,

        color: color,

        quantity:
          Number(quantity),

        design_id:
          design.design_id,

        quality:
          quality || null,

        price:
          Number(priced.data.unit_price),

        total_price:
          Number(priced.data.total_price),

        original_price:
          Number(priced.data.original_unit_price ?? priced.data.unit_price),

        discount_percent:
          Number(priced.data.discount_percent || 0),

        delivery_charge:
          Number(design.delivery_charge || 0),

        product_type:
          group,
      });
      toast.success("Added to cart.");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        "Could not calculate the price."
      );
    } finally {
      setAdding(false);
    }
  };

  /*
    No design selected
  */

  if (!designId) {
    return (
      <div className="container-pmt py-16">
        <div className="overline">
          Products
        </div>

        <h1 className="display-h2 mt-2">
          Choose a design first.
        </h1>

        <p className="mt-4 text-stone-600">
          Select a design from our
          collection to buy it.
        </p>

        <Link
          to="/designs"
          className="btn-primary mt-8 inline-flex"
        >
          <ArrowLeft size={16} />
          Browse Designs
        </Link>
      </div>
    );
  }

  /*
    Loading
  */

  if (loading) {
    return (
      <div className="container-pmt py-16 text-center">
        <div className="text-stone-500">
          Loading design...
        </div>
      </div>
    );
  }

  /*
    Design not found
  */

  if (!design) {
    return (
      <div className="container-pmt py-16 text-center">
        <div className="overline">
          Products
        </div>

        <h1 className="display-h2 mt-2">
          Design not found
        </h1>

        <p className="mt-4 text-stone-600">
          This design may have been
          removed or is currently
          unavailable.
        </p>

        <Link
          to="/designs"
          className="btn-primary mt-8 inline-flex"
        >
          <ArrowLeft size={16} />
          Back to Designs
        </Link>
      </div>
    );
  }

  return (
    <div
      className="container-pmt py-12"
      data-testid="products-page"
    >
      {/* BACK */}

      <Link
        to="/designs"
        className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500 hover:text-stone-900"
      >
        <ArrowLeft size={14} />
        Back to Designs
      </Link>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-10">

        {/* =========================
            DESIGN IMAGE
        ========================= */}

        <div>
          <div
            className="relative aspect-[4/5] bg-[#F3EDE2] overflow-hidden border border-[#E6DFD5] touch-pan-y select-none"
            onTouchStart={handleGalleryTouchStart}
            onTouchEnd={handleGalleryTouchEnd}
            onTouchCancel={() => { touchStartX.current = null; touchStartY.current = null; }}
          >
            {selectedImage ? (
              <BlurHashImage
                src={selectedImage}
                blurHash={design.blurhash}
                alt={design.name}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-stone-400">
                No image
              </div>
            )}
          </div>

          <div className="mt-3 text-[10px] leading-relaxed text-stone-500">
            📌 Product images are for reference. Actual product colour, fabric, and print may vary slightly due to screen settings and production.
          </div>

          <div className="mt-3 grid grid-cols-5 gap-2">
            {galleryImages.map((im, i) => (
              <button
                type="button"
                key={im}
                onClick={() => setSelectedImage(im)}
                className={`border ${selectedImage === im ? "border-[#141413]" : "border-[#E6DFD5]"}`}
              >
                <BlurHashImage
                  src={im}
                  blurHash={design.blurhash}
                  alt={design.name + " view " + (i + 1)}
                  className="aspect-square w-full object-contain bg-[#F3EDE2]"
                />
              </button>
            ))}
          </div>
        </div>

        {/* =========================
            PRODUCT DETAILS
        ========================= */}

        <div>

          <div className="flex items-start justify-between gap-4">
            <div className="overline">{group}</div>
            <div className="flex items-center gap-2">
              <ShareButton
                title={design.name}
                url={`/products?design=${encodeURIComponent(design.design_id)}`}
              />
              <button
                type="button"
                onClick={() => toggleWishlist({
                  type: "design",
                  id: design.design_id,
                  name: design.name,
                  image: imageUrl(design.image_url || design.images?.[0] || null),
                  price: design.price,
                  category: design.category,
                  group: design.group,
                })}
                className="w-11 h-11 shrink-0 rounded-full border border-[#E6DFD5] bg-white flex items-center justify-center hover:text-[#D95328]"
                aria-label={isWishlisted("design", design.design_id) ? "Remove from wishlist" : "Add to wishlist"}
                data-testid="design-detail-wishlist"
              >
                <Heart size={20} fill={isWishlisted("design", design.design_id) ? "currentColor" : "none"} className={isWishlisted("design", design.design_id) ? "text-[#D95328]" : ""} />
              </button>
            </div>
          </div>

          {/* DESIGN NAME */}

          <h1
            className="mt-2 text-4xl font-bold uppercase"
            style={{
              fontFamily:
                "Barlow Condensed, sans-serif",
            }}
          >
            {design.name}
          </h1>

          {/* DESIGN ID */}

          <div className="mt-2 text-xs font-mono uppercase tracking-widest text-stone-500">
            {design.design_id}
          </div>

          {/* DESCRIPTION */}

          {design.description && (
            <p className="mt-4 text-stone-600">
              {design.description}
            </p>
          )}

          {/* =========================
              PRICE
          ========================= */}

          <div className="mt-6">
            <div className="text-xs uppercase tracking-wider text-stone-500">
              Price
            </div>

            {discountPercent > 0 && baseUnitPrice > 0 && (
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <span className="text-stone-500 line-through text-base">
                  ₹{baseUnitPrice.toLocaleString("en-IN")}
                </span>
                <span className="text-xs font-bold bg-red-100 text-red-700 px-2 py-1">
                  {discountPercent}% OFF
                </span>
              </div>
            )}
            <div
              className="mt-1 text-3xl font-bold"
              style={{
                fontFamily:
                  "Barlow Condensed, sans-serif",
              }}
            >
              ₹
              {unitPrice.toLocaleString(
                "en-IN"
              )}
            </div>
          </div>

          {/* =========================
              QUALITY / GSM
          ========================= */}

          {qualities.length > 0 && (
            <div className="mt-8">
              <div className="label-pmt">
                T-Shirt Quality / GSM
              </div>

              <div className="flex flex-wrap gap-2 mt-2">
                {qualities.map((entry) => {
                  const name = qualityName(entry);
                  const price = qualityPrice(entry);

                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setQuality(name)}
                      className={`px-3 py-2 text-xs font-semibold uppercase border ${
                        quality === name
                          ? "bg-[#141413] text-[#FAF7F2] border-[#141413]"
                          : "bg-white border-[#E6DFD5] hover:border-[#141413]"
                      }`}
                    >
                      {name}
                      {price != null
                        ? ` — ₹${Math.round(price * (1 - Math.min(100, Math.max(0, discountPercent)) / 100)).toLocaleString("en-IN")}`
                        : ""}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* =========================
              SIZE
          ========================= */}

          <div className="mt-8">
            <div className="label-pmt">
              Size
            </div>

            <select
              className="input-pmt mt-2"
              value={size}
              onChange={(e) =>
                setSize(
                  e.target.value
                )
              }
            >
              {sizes.map(
                (item) => (
                  <option
                    key={item}
                    value={item}
                  >
                    {item}
                  </option>
                )
              )}
            </select>
          </div>

          {/* =========================
              COLOUR
              ADMIN CONTROLLED
          ========================= */}

          <div ref={sizeChartAreaRef} className="mt-2">
            <button type="button" onClick={() => setShowSizeChart((current) => !current)} aria-expanded={showSizeChart} aria-controls="design-size-chart-panel" className="inline-flex items-center gap-2 text-sm font-semibold text-[#D95328] hover:underline"><Ruler size={16} />{showSizeChart ? "Hide Size Chart" : "View Size Chart"}</button>
            <div hidden={!showSizeChart}>
              <section id="design-size-chart-panel" className="mt-3 max-w-xl border border-[#E6DFD5] bg-white p-2 sm:p-3" aria-label="Size measurement chart">
          <p className="mb-2 text-xs text-stone-500">Check chest, length and shoulder measurements before choosing your size.</p>
          {sizeChart ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-[#F8F5F0]"><tr>{(sizeChart.headers.length >= 4 && !sizeChart.headers.some((h) => /chest|length|shoulder/i.test(String(h))) ? ["Size", "Chest", "Length", "Shoulder", ...sizeChart.headers.slice(4)] : sizeChart.headers).map((heading, index) => <th key={index} className="px-2 py-1.5 text-left font-semibold border-b border-[#E6DFD5] whitespace-nowrap">{heading || ["Size", "Chest", "Length", "Shoulder"][index] || `Measurement ${index + 1}`}</th>)}</tr></thead>
                  <tbody>{sizeChart.rows.map((row, rowIndex) => <tr key={rowIndex} className="border-b border-[#E6DFD5]">{(sizeChart.headers.length >= 4 && !sizeChart.headers.some((h) => /chest|length|shoulder/i.test(String(h))) ? ["Size", "Chest", "Length", "Shoulder", ...sizeChart.headers.slice(4)] : sizeChart.headers).map((_, index) => <td key={index} className="px-2 py-1.5 whitespace-nowrap">{row[index] ?? "—"}</td>)}</tr>)}</tbody>
                </table>
              </div>
              <div className="mt-3 border-t border-[#E6DFD5] pt-2 text-xs leading-relaxed text-stone-600">
                <h3 className="mb-1 font-semibold text-stone-900">Measurement Guidelines</h3>
                <p><strong>Chest:</strong> Measure straight across the garment under the armholes.</p>
                <p><strong>Length:</strong> Measure from the highest shoulder point beside the neck to the bottom hem.</p>
                <p><strong>Shoulder:</strong> Measure across the back from one shoulder seam to the other.</p>
                <p className="mt-1">Lay the garment flat without stretching it and compare in the same units shown in the chart.</p>
              </div>
            </>
          ) : (
            <p className="text-xs text-stone-500">Size measurements have not been added yet.</p>
          )}
              </section>
            </div>
          </div>

          <div className="mt-6">
            <div className="label-pmt">
              Colour
            </div>

            {colors.length === 1 ? (
              <div className="input-pmt mt-2 flex items-center">
                {colors[0]}
              </div>
            ) : colors.length > 1 ? (
              <select
                className="input-pmt mt-2"
                value={color}
                onChange={(e) =>
                  setColor(
                    e.target.value
                  )
                }
              >
                {colors.map(
                  (item) => (
                    <option
                      key={item}
                      value={item}
                    >
                      {item}
                    </option>
                  )
                )}
              </select>
            ) : (
              <div className="mt-2 p-3 border border-yellow-200 bg-yellow-50 text-sm text-yellow-800">
                No colours have
                been configured
                for this design.
                Please contact
                PrintMyTees.
              </div>
            )}
          </div>

          {/* =========================
              QUANTITY
          ========================= */}

          <div className="mt-6">
            <div className="label-pmt">
              Quantity
            </div>

            <div className="mt-2 flex items-center border border-[#E6DFD5] bg-white h-11 w-fit">
              <button
                type="button"
                onClick={() => setQuantity((current) => Math.max(1, Number(current || 1) - 1))}
                className="w-11 h-full text-lg font-semibold text-stone-700 hover:bg-[#F3EDE2]"
                aria-label="Decrease quantity"
              >
                −
              </button>
              <div
                className="w-14 h-full flex items-center justify-center text-sm font-semibold border-x border-[#E6DFD5]"
                aria-label="Quantity"
              >
                {quantity || 1}
              </div>
              <button
                type="button"
                onClick={() => setQuantity((current) => Math.min(1000, Number(current || 1) + 1))}
                className="w-11 h-full text-lg font-semibold text-stone-700 hover:bg-[#F3EDE2]"
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          </div>

          {/* =========================
              ORDER SUMMARY
          ========================= */}

          <div className="mt-8 p-5 bg-[#F3EDE2] border border-[#E6DFD5]">

            <div className="flex justify-between text-sm text-stone-600">
              <span>
                Unit price
              </span>

              <span>
                ₹
                {unitPrice.toLocaleString(
                  "en-IN"
                )}
              </span>
            </div>

            <div className="mt-2 flex justify-between text-sm text-stone-600">
              <span>
                Quantity
              </span>

              <span>
                {safeQuantity}
              </span>
            </div>

            <div className="mt-4 pt-4 border-t border-[#D9D0C4] flex justify-between items-end">
              <span className="font-bold uppercase">
                Product total for {safeQuantity} {safeQuantity === 1 ? "item" : "items"}
              </span>

              <span
                className="text-3xl font-bold"
                style={{
                  fontFamily:
                    "Barlow Condensed, sans-serif",
                }}
              >
                ₹
                {totalPrice.toLocaleString(
                  "en-IN"
                )}
              </span>
            </div>
          </div>

          <div className="mt-3 p-4 bg-[#FFF7D6] border border-[#E8D58A] text-sm text-stone-800">
            <div className="font-semibold">GST and Delivery Charges</div>
            <p className="mt-1">GST and delivery charges are added or applicable at checkout.</p>
          </div>

          <div className="p-4 bg-[#F8F5F0] border border-[#E6DFD5] text-xs text-stone-700">
            <div className="font-semibold text-stone-800">Made to order</div>
            <p className="mt-1">Your selected size, colour, quality and customisation details are prepared according to your order.</p>
          </div>

          {/* =========================
              ADD TO CART
          ========================= */}

          <button
            type="button"
            onClick={
              handleAddToCart
            }
            disabled={
              !unitPrice ||
              !color ||
              !size ||
              adding
            }
            className="btn-primary mt-6 w-full justify-center disabled:opacity-50"
            data-testid="product-add-to-cart"
          >
            <ShoppingBag
              size={16}
            />

            {adding
              ? "Added to Cart"
              : "Add to Cart"}
          </button>

          {adding && (
            <p className="mt-2 text-sm font-semibold text-green-700 text-center" role="status">
              ✓ Added to cart successfully
            </p>
          )}

          <Link
            to="/cart"
            className="btn-secondary mt-3 w-full justify-center"
            data-testid="product-go-cart"
          >
            Go to Cart
          </Link>

          {/* PAYMENT NOTE */}

          <p className="mt-3 text-xs text-stone-500 text-center">
            Online payment is
            available at checkout.
          </p>

        </div>
      </div>

    </div>
  );
}